import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Grid,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Autocomplete,
    TextField as MuiTextField,
    InputAdornment,
    Popper,
    FormHelperText
} from '@mui/material';
import { DatePicker, TextField } from '@finpay-development/shared-components';
import { useFormik } from 'formik';
import SearchIcon from '@mui/icons-material/Search';
import { AppDispatch } from '../../../../../shared/state/store';
import { RootState } from '../../../../../shared/state/root-reducer';
import { showErrorStatus, showStatus } from '@finpay-development/shared-platform/lib/esm/security/state/user-slice';
import { savePatientCurrentInsurance, getAllPatientCurrentInsurances, savePatient, getPatientDemographics } from '@finpay-development/shared-platform/lib/esm/patient/state/patient-thunk';
import { PatientCurrentInsurance, emptyPatientCurrentInsurance, PatientViewModel } from '@finpay-development/shared-platform/lib/esm/patient/components/models/patient-view-model';
import { PhoneType } from '@finpay-development/shared-platform/lib/esm/shared/model/primary-contact';
import { Utils } from '@finpay-development/shared-platform/lib/esm/shared/utils';
import { PatientViewModel as PatientVM } from '@finpay-development/shared-platform/lib/esm/patient/components/models/patient-view-model';

interface AvailityErrorDialogProps {
    open: boolean;
    onClose: () => void;
    availityErrorMessage: string | null;
    selectedPatient: PatientVM | null;
    selectedPatientCurrentInsurance: PatientCurrentInsurance | null;
    isRunningAvailityVOB: boolean;
    onRunAvailityVob: (overrideInsurance?: PatientCurrentInsurance | null, overridePatient?: PatientVM | null) => Promise<void>;
}

export const AvailityErrorDialog: React.FC<AvailityErrorDialogProps> = ({
    open,
    onClose,
    availityErrorMessage,
    selectedPatient,
    selectedPatientCurrentInsurance,
    isRunningAvailityVOB,
    onRunAvailityVob
}) => {
    const dispatch = useDispatch<AppDispatch>();
    const [isSavingInsurance, setIsSavingInsurance] = useState(false);
    
    const reduxMasterPayors = useSelector((state: RootState) => 
        state.adminContext.adminConfigurationContext?.payersMap
    ) || new Map();

    const mapToInsuranceObj = (formValues: any): PatientCurrentInsurance => {
        return {
            patientCurrentInsuranceId: formValues.patientCurrentInsuranceId || 0,
            patientId: formValues.patientId,
            payorId: formValues.payorId || 0,
            payorPlanId: formValues.payorPlanId || 0,
            subscriberName: formValues.subscriberName || '',
            subscriberRelation: formValues.subscriberRelation || 0,
            memberId: formValues.memberId || '',
            groupNumber: formValues.groupNumber || '',
        };
    };

    const parseDateString = (dateString: string | Date | null | undefined): Date | null => {
        if (!dateString) return null;
        if (dateString instanceof Date) return dateString;
        if (typeof dateString === 'string') {
            const parts = dateString.split('-');
            if (parts.length === 3) {
                const year = parseInt(parts[0], 10);
                const month = parseInt(parts[1], 10) - 1;
                const day = parseInt(parts[2], 10);
                return new Date(year, month, day);
            }
            return new Date(dateString);
        }
        return null;
    };

    const dialogFormik = useFormik({
        initialValues: {
            ...(selectedPatientCurrentInsurance || emptyPatientCurrentInsurance),
            firstName: selectedPatient?.contact?.firstName || '',
            lastName: selectedPatient?.contact?.lastName || '',
            dateOfBirth: parseDateString(selectedPatient?.contact?.birthDate),
        },
        enableReinitialize: true,
        onSubmit: async (values) => {
            if (!selectedPatient?.patientId) return;

            const errors: any = {};
            if (!values.firstName || values.firstName.trim() === '') {
                errors.firstName = 'First Name is required';
            }
            if (!values.lastName || values.lastName.trim() === '') {
                errors.lastName = 'Last Name is required';
            }
            if (!values.dateOfBirth) {
                errors.dateOfBirth = 'Date of Birth is required';
            }
            if (!values.payorId || values.payorId === 0) {
                errors.payorId = 'Payer is required';
            }
            if (!values.payorPlanId || values.payorPlanId === 0) {
                errors.payorPlanId = 'Plan is required';
            }
            if (!values.memberId || (typeof values.memberId === 'string' && values.memberId.trim() === '')) {
                errors.memberId = 'Member ID is required';
            }

            if (Object.keys(errors).length > 0) {
                dialogFormik.setErrors(errors);
                dialogFormik.setTouched({
                    firstName: true,
                    lastName: true,
                    dateOfBirth: true,
                    payorId: true,
                    payorPlanId: true,
                    memberId: true,
                });
                dispatch(showErrorStatus('Please fill in all required fields'));
                return;
            }
            
            setIsSavingInsurance(true);
            try {
                const formattedNewBirthDate = values.dateOfBirth 
                    ? (() => {
                        const date = new Date(values.dateOfBirth);
                        const year = date.getFullYear();
                        const month = String(date.getMonth() + 1).padStart(2, '0');
                        const day = String(date.getDate()).padStart(2, '0');
                        return `${year}-${month}-${day}`;
                    })()
                    : '';
                const hasPatientInfoChanged = 
                    values.firstName !== selectedPatient?.contact?.firstName ||
                    values.lastName !== selectedPatient?.contact?.lastName ||
                    formattedNewBirthDate !== (selectedPatient?.contact?.birthDate || '');

                let refreshPatientResult: PatientVM | null = null;
                if (hasPatientInfoChanged) {
                    const currentContact = selectedPatient.contact;
                    const currentAddress = currentContact?.primaryAddress;
                    const currentIncome = currentContact?.contactIncome;

                    const cleanAddress = currentAddress ? {
                        streetAddress1: currentAddress.streetAddress1 || '',
                        streetAddress2: currentAddress.streetAddress2 || '',
                        city: currentAddress.city || '',
                        state: currentAddress.state || { stateId: 0, stateCode: '', stateName: '' },
                        zipCode: currentAddress.zipCode || '',
                        zipCode4: currentAddress.zipCode4 || '',
                        addressType: currentAddress.addressType || 'Primary'
                    } : {
                        streetAddress1: '',
                        streetAddress2: '',
                        city: '',
                        state: { stateId: 0, stateCode: '', stateName: '' },
                        zipCode: '',
                        zipCode4: '',
                        addressType: 'Primary'
                    };

                    const cleanContactIncome = currentIncome ? {
                        yearlySalary: currentIncome.yearlySalary || 0,
                        employer: currentIncome.employer || '',
                        yearsAtEmployer: currentIncome.yearsAtEmployer || 0,
                        verificationScore: currentIncome.verificationScore || 0,
                        retirementIncome: currentIncome.retirementIncome || 0,
                        capacityToPay: currentIncome.capacityToPay || 0,
                        disposableIncome: currentIncome.disposableIncome || 0,
                        businessIncome: currentIncome.businessIncome || 0,
                        wealthIncome: currentIncome.wealthIncome || 0,
                        wagesAndEarnings: currentIncome.wagesAndEarnings || 0,
                        isEmployed: currentIncome.isEmployed || false,
                        clientEmployerId: currentIncome.clientEmployerId || 0,
                        employmentStatusId: currentIncome.employmentStatusId || 0
                    } : {
                        yearlySalary: 0,
                        employer: '',
                        yearsAtEmployer: 0,
                        verificationScore: 0,
                        retirementIncome: 0,
                        capacityToPay: 0,
                        disposableIncome: 0,
                        businessIncome: 0,
                        wealthIncome: 0,
                        wagesAndEarnings: 0,
                        isEmployed: false,
                        clientEmployerId: 0,
                        employmentStatusId: 0
                    };

                    const updatedContact = {
                        title: currentContact?.title || '',
                        firstName: values.firstName || currentContact?.firstName || '',
                        lastName: values.lastName || currentContact?.lastName || '',
                        middleName: currentContact?.middleName || '',
                        preferredName: currentContact?.preferredName || '',
                        primaryAddress: cleanAddress,
                        email: currentContact?.email || '',
                        primPhoneNum: currentContact?.primPhoneNum || '',
                        primPhoneType: currentContact?.primPhoneType || PhoneType.mobile,
                        secPhoneNum: currentContact?.secPhoneNum || '',
                        secPhoneType: currentContact?.secPhoneType || PhoneType.home,
                        leaveVMFl: currentContact?.leaveVMFl || false,
                        smsConsentFl: currentContact?.smsConsentFl || false,
                        ssn: currentContact?.ssn || '',
                        ssnLast4: currentContact?.ssnLast4 || '',
                        birthDate: formattedNewBirthDate || currentContact?.birthDate || '',
                        contactIncome: cleanContactIncome
                    };

                    const patientToSave = {
                        patientId: selectedPatient.patientId,
                        contact: updatedContact,
                    };

                    await dispatch(savePatient({ patient: patientToSave })).unwrap();
                    const refreshResult = await dispatch(getPatientDemographics(selectedPatient.patientId)).unwrap();
                    refreshPatientResult = refreshResult?.patientDetails || null;
                }

                const existingInsuranceId = values.patientCurrentInsuranceId || selectedPatientCurrentInsurance?.patientCurrentInsuranceId;
                if (!existingInsuranceId || existingInsuranceId === 0) {
                    dispatch(showErrorStatus('Cannot update: No existing insurance record found'));
                    return;
                }
                
                const insuranceToSave = mapToInsuranceObj({
                    ...values,
                    patientId: selectedPatient.patientId,
                    patientCurrentInsuranceId: existingInsuranceId
                });
                await dispatch(savePatientCurrentInsurance({ insurance: insuranceToSave })).unwrap();
                const refreshResult = await dispatch(getAllPatientCurrentInsurances({ patientId: selectedPatient.patientId })).unwrap();
                dispatch(showStatus('Information saved successfully'));
                onClose();
                const latestInsurance = refreshResult && refreshResult.length > 0 ? refreshResult[0] : null;
                const latestPatient = (hasPatientInfoChanged && refreshPatientResult) ? refreshPatientResult : selectedPatient;
                await onRunAvailityVob(latestInsurance, latestPatient);
            } catch (error: any) {
                const message = error instanceof Error ? error.message : String(error);
                console.error('Error saving information:', error);
                dispatch(showErrorStatus(message || 'Failed to save information'));
            } finally {
                setIsSavingInsurance(false);
            }
        }
    });

    const handleRunAvaility = () => {
        dialogFormik.handleSubmit();
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth={true}
            maxWidth="md"
            className="modal"
            PaperProps={{
                sx: {
                    margin: 0,
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                }
            }}
        >
            <DialogTitle sx={{ textAlign: 'center' }}>Availity Pull Issues</DialogTitle>
            <DialogContent>
                {availityErrorMessage && (
                    <Typography 
                        variant="body2" 
                        color="error" 
                        sx={{ mb: 2, textAlign: 'center' }}
                    >
                        {availityErrorMessage}
                    </Typography>
                )}
                <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={6}>
                        <TextField
                            name="firstName"
                            placeholder="First Name"
                            value={dialogFormik.values.firstName}
                            onChange={dialogFormik.handleChange}
                            onBlur={dialogFormik.handleBlur}
                            label="First Name"
                            className={`state-field ${dialogFormik.touched.firstName && Boolean(dialogFormik.errors.firstName) ? 'error-border' : 'required-fields'}`}
                            error={dialogFormik.touched.firstName && Boolean(dialogFormik.errors.firstName)}
                            helperText={dialogFormik.touched.firstName && dialogFormik.errors.firstName ? String(dialogFormik.errors.firstName) : undefined}
                            required
                            test-id="availity-dialog-first-name"
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            name="lastName"
                            placeholder="Last Name"
                            value={dialogFormik.values.lastName}
                            onChange={dialogFormik.handleChange}
                            onBlur={dialogFormik.handleBlur}
                            label="Last Name"
                            className={`state-field ${dialogFormik.touched.lastName && Boolean(dialogFormik.errors.lastName) ? 'error-border' : 'required-fields'}`}
                            error={dialogFormik.touched.lastName && Boolean(dialogFormik.errors.lastName)}
                            helperText={dialogFormik.touched.lastName && dialogFormik.errors.lastName ? String(dialogFormik.errors.lastName) : undefined}
                            required
                            test-id="availity-dialog-last-name"
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <Autocomplete
                            getOptionLabel={(option) => option.payorName}
                            value={reduxMasterPayors.get(dialogFormik.values.payorId) || null}
                            onChange={(e, value) => {
                                const selectedPayorId = value?.payorId;
                                dialogFormik.setFieldValue('payorId', selectedPayorId || 0);

                                if (selectedPayorId) {
                                    const selectedPayor = reduxMasterPayors.get(selectedPayorId);
                                    const allPlan = selectedPayor?.payorPlans?.find(plan => 
                                        plan.planName?.toUpperCase() === 'ALL'
                                    );
                                    if (allPlan) {
                                        dialogFormik.setFieldValue('payorPlanId', allPlan.payorPlanId);
                                    } else {
                                        dialogFormik.setFieldValue('payorPlanId', 0);
                                    }
                                } else {
                                    dialogFormik.setFieldValue('payorPlanId', 0);
                                }
                            }}
                            renderInput={(params) => {
                                return (
                                    <MuiTextField
                                        {...params}
                                        InputProps={{
                                            ...params.InputProps,
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <SearchIcon/>
                                                </InputAdornment>
                                            ),
                                        }}
                                        label="Payer"
                                        placeholder="Search Payers"
                                        error={dialogFormik.touched.payorId && Boolean(dialogFormik.errors.payorId)}
                                        helperText={dialogFormik.touched.payorId && dialogFormik.errors.payorId ? String(dialogFormik.errors.payorId) : undefined}
                                    />
                                );
                            }}
                            onBlur={() => dialogFormik.setFieldTouched('payorId', true)}
                            PopperComponent={(props) => {
                                return (
                                    <Popper {...props} placement="bottom-start"
                                            modifiers={[{name: 'flip', enabled: false}]}/>
                                );
                            }}
                            options={Array.from(reduxMasterPayors.values()).filter((payor) => !payor.isRateAsPayor && payor.payorPlans && payor.payorPlans.length > 0)}
                            test-id="availity-dialog-payor"
                            className={`${dialogFormik.touched.payorId && Boolean(dialogFormik.errors.payorId) ?
                                'error-border' :
                                'required-fields'}`}
                        />
                    </Grid>
                    <Grid item xs={6} className="MuiTextField-root">
                        <FormControl fullWidth size="small">
                            <InputLabel id="availity-plan-select">Plan</InputLabel>
                            <Select
                                labelId="availity-plan-select"
                                label="Plan"
                                value={dialogFormik.values.payorPlanId}
                                onChange={(e) => {
                                    let planValue = (e.target as HTMLInputElement).value;
                                    if (planValue) {
                                        dialogFormik.setFieldValue('payorPlanId', planValue);
                                    }
                                }}
                                style={{
                                    width: '100%',
                                }}
                                renderValue={(selected) => {
                                    const selectedPlan = reduxMasterPayors.get(dialogFormik.values.payorId)?.
                                        payorPlans.
                                        find((plan) => {
                                            return plan.payorPlanId === selected;
                                        });
                                    return selectedPlan?.planName || 'Select Plan';
                                }}
                                className={`state-field ${dialogFormik.touched.payorPlanId &&
                                Boolean(dialogFormik.errors.payorPlanId) ?
                                    'error-border' :
                                    'required-fields'}`}
                                error={dialogFormik.touched.payorPlanId && Boolean(dialogFormik.errors.payorPlanId)}
                                onBlur={() => dialogFormik.setFieldTouched('payorPlanId', true)}
                                required
                                test-id="availity-dialog-plan"
                            >
                                {
                                    reduxMasterPayors.get(dialogFormik.values.payorId)?.
                                        payorPlans?.
                                        map((plan) => {
                                            return (
                                                <MenuItem
                                                    key={plan.payorPlanId}
                                                    value={plan.payorPlanId}
                                                >
                                                    {plan.planName}
                                                </MenuItem>
                                            );
                                        }) || []
                                }
                            </Select>
                            {dialogFormik.touched.payorPlanId && dialogFormik.errors.payorPlanId && (
                                <FormHelperText error>{String(dialogFormik.errors.payorPlanId)}</FormHelperText>
                            )}
                        </FormControl>
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            name="memberId"
                            placeholder="Enter Member ID"
                            value={dialogFormik.values.memberId}
                            onChange={dialogFormik.handleChange}
                            onBlur={dialogFormik.handleBlur}
                            label="Member ID"
                            className={`state-field ${dialogFormik.touched.memberId && Boolean(dialogFormik.errors.memberId) ?
                                'error-border' :
                                'required-fields'}`}
                            error={dialogFormik.touched.memberId && Boolean(dialogFormik.errors.memberId)}
                            helperText={dialogFormik.touched.memberId && dialogFormik.errors.memberId ? String(dialogFormik.errors.memberId) : undefined}
                            required
                            test-id="availity-dialog-member-id"
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <DatePicker
                            label="Date of Birth"
                            className={`date state-field ${dialogFormik.touched.dateOfBirth && Boolean(dialogFormik.errors.dateOfBirth) ? 'error-border' : 'required-fields'}`}
                            value={dialogFormik.values.dateOfBirth}
                            onChange={(date: Date) => {
                                if (Utils.isValidDate(date) && !Utils.isFutureDate(date)) {
                                    dialogFormik.setFieldValue('dateOfBirth', date);
                                    dialogFormik.setFieldTouched('dateOfBirth', true);
                                }
                            }}
                            error={dialogFormik.touched.dateOfBirth && Boolean(dialogFormik.errors.dateOfBirth)}
                            minDate={Utils.getMinDate()}
                            maxDate={new Date()}
                            data="availity-dialog-date-of-birth"
                        />
                        {dialogFormik.touched.dateOfBirth && dialogFormik.errors.dateOfBirth && (
                            <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block', ml: 1.5 }}>
                                {String(dialogFormik.errors.dateOfBirth)}
                            </Typography>
                        )}
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button 
                    onClick={onClose}
                    test-id="availity-error-dialog-close-button"
                >
                    Close
                </Button>
                <Button 
                    onClick={handleRunAvaility}
                    disabled={isSavingInsurance || isRunningAvailityVOB}
                    variant="contained"
                    test-id="availity-error-dialog-save-button"
                >
                    {isSavingInsurance ? 'Saving...' : isRunningAvailityVOB ? 'Running Availity...' : 'Run Availity'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

